import {
  NavLink,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard,
  Truck,
  ClipboardList,
  Inbox,
  CheckSquare,
  Wrench,
  Package,
  FileText,
  Radio,
  BarChart3,
  Sun,
  Moon,
  LogOut,
  Menu,
  X,
  Users,
  Fuel,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// A versão escura usa letras claras; a versão original é para fundos claros.
function BrandLogo({ className = "" }) {
  return (
    <>
      <img src="/martiterra-logo-original.png" alt="Martiterra Engenharia" className={`${className} block dark:hidden object-contain`} />
      <img src="/martiterra-logo-sidebar.png" alt="Martiterra Engenharia" className={`${className} hidden dark:block object-contain`} />
    </>
  );
}
// ============================================================
// MATRIZ DE ACESSO
//
// role:
// - admin
// - gestor
// - colaborador
//
// sub_role:
// - motorista
// - abastecedor
// - mecanico
// - operador
// ============================================================
const NAV = [
  {
    to: "/",
    label: "Painel",
    icon: LayoutDashboard,
    show: () => true,
  },
  {
  to: "/inbox",
  label: "Minhas Inspeções",
  icon: Inbox,
  show: (role) =>
    role === "admin" ||
    role === "colaborador",
  },
  {
    to: "/vehicles",
    label: "Frota",
    icon: Truck,
    show: (role, subRole) =>
      role === "admin" ||
      role === "gestor" ||
      (role === "colaborador" &&
        [
          "motorista",
          "mecanico",
          "abastecedor",
          "operador",
        ].includes(subRole)),
  },
  {
    to: "/collaborators",
    label: "Colaboradores",
    icon: Users,
    show: (role) =>
      role === "admin" ||
      role === "gestor",
  },
  {
    to: "/templates",
    label: "Checklists",
    icon: ClipboardList,
    show: (role) =>
      role === "admin" ||
      role === "gestor",
  },
  {
    to: "/approvals",
    label: "Inspeções",
    icon: CheckSquare,
    show: (role) =>
      role === "admin" ||
      role === "gestor",
  },
  {
    to: "/fuel",
    label: "Estoque de Combustível",
    icon: Fuel,
    show: (role, subRole) =>
      role === "admin" ||
      role === "gestor" ||
      (role === "colaborador" &&
        subRole === "abastecedor"),
  },
  {
    to: "/workshops",
    label: "Oficinas",
    icon: Wrench,
    show: (role, subRole) =>
      role === "admin" ||
      role === "gestor" ||
      (role === "colaborador" &&
        subRole === "mecanico"),
  },
  {
    to: "/parts",
    label: "Peças",
    icon: Package,
    show: (role, subRole) =>
      role === "admin" ||
      role === "gestor" ||
      (role === "colaborador" &&
        subRole === "mecanico"),
  },
  {
    to: "/quotes",
    label: "Orçamentos",
    icon: FileText,
    show: (role, subRole) =>
      role === "admin" ||
      role === "gestor" ||
      (role === "colaborador" &&
        subRole === "mecanico"),
  },
  {
    to: "/integration",
    label: "Fleetrun/Wialon",
    icon: Radio,
    // SOMENTE ADMIN
    show: (role) =>
      role === "admin",
  },
  {
    to: "/reports",
    label: "Relatórios",
    icon: BarChart3,
    show: (role) =>
      role === "admin" ||
      role === "gestor",
  },
];
const ROLE_LABEL = {
  admin: "Administrador",
  gestor: "Gestor",
  colaborador: "Colaborador",
  // Compatibilidade temporária
  // com registros antigos.
  operador: "Colaborador",
  mecanico: "Colaborador",
};
const SUB_ROLE_LABEL = {
  motorista: "Motorista",
  abastecedor: "Abastecedor",
  mecanico: "Mecânico",
  operador: "Operador de Máquinas",
};
export default function Layout() {
  const { user, logout } =
    useAuth();
  const { theme, toggle } =
    useTheme();
  const nav = useNavigate();
  const [open, setOpen] =
    useState(false);
  if (!user) {
    return null;
  }
  // ============================================================
  // NORMALIZAÇÃO
  //
  // Mantém compatibilidade com usuários antigos enquanto
  // o banco termina de ser migrado para:
  //
  // role = colaborador
  // sub_role = ...
  // ============================================================
  let role =
    user.role || "";
  let subRole =
    user.sub_role || "";
  if (role === "mecanico") {
    role = "colaborador";
    if (!subRole) {
      subRole = "mecanico";
    }
  }
  if (role === "operador") {
    role = "colaborador";
    if (!subRole) {
      subRole = "operador";
    }
  }
  const items =
    NAV.filter((item) =>
      item.show(
        role,
        subRole
      )
    );
  const roleLabel =
    ROLE_LABEL[role] ||
    role;
  const subLabel =
    SUB_ROLE_LABEL[subRole] ||
    "";
  // Admin e Gestor aparecem apenas com o próprio cargo.
  // Colaborador aparece como:
  // Colaborador · Motorista
  // Colaborador · Mecânico
  // etc.
  const badge =
    role === "colaborador" &&
    subLabel
      ? `${roleLabel} · ${subLabel}`
      : roleLabel;
  const handleLogout =
    async () => {
      await logout();
      nav("/login");
    };
  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* ======================================================
          SIDEBAR DESKTOP
      ====================================================== */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#E2E5E9] dark:border-[#333333] bg-[#F8F9FA] dark:bg-[#171717] text-[#292929] dark:text-[#E6E6E6]">
        {/* MARCA MARTITERRA */}
        <div className="px-4 py-5 border-b border-[#E2E5E9] dark:border-[#333333]">
          <BrandLogo className="w-full max-w-[215px] h-auto" />
        </div>

        {/* NAVEGAÇÃO */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const Icon =
              item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={
                  item.to === "/"
                }
                data-testid={`nav-${
                  item.to.replace(
                    "/",
                    ""
                  ) || "home"
                }`}
                className={({
                  isActive,
                }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#FFC629] text-[#171717] shadow-sm"
                      : "text-[#444444] dark:text-[#D4D4D4] hover:bg-[#E9EDF0] dark:hover:bg-[#292929] hover:text-[#078348] dark:hover:text-[#FFC629]"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </nav>
        {/* USUÁRIO */}
        <div className="p-3 border-t border-[#E2E5E9] dark:border-[#333333]">
          <div className="flex items-center justify-between mb-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">
                {user.name}
              </div>
              <Badge
                variant="outline"
                className="mt-0.5 text-[10px]"
                data-testid="role-badge"
              >
                {badge}
              </Badge>
            </div>
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              className="p-2 rounded-md hover:bg-[#E9EDF0] dark:hover:bg-[#292929] text-[#078348] dark:text-[#FFC629]"
              aria-label="Alternar tema"
            >
              {theme === "dark" ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={
              handleLogout
            }
            data-testid="logout-btn"
            className="w-full gap-2"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </Button>
        </div>
      </aside>
      {/* ======================================================
          TOPO MOBILE
      ====================================================== */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#F8F9FA] text-[#292929] dark:bg-[#171717] dark:text-white border-b border-[#E2E5E9] dark:border-[#333333] flex items-center justify-between px-4 h-14">
        <BrandLogo className="w-[164px] h-auto" />

        <div className="flex items-center gap-1">
          <button
            onClick={toggle}
            className="p-2 text-[#078348] dark:text-[#FFC629]"
            aria-label="Tema"
            data-testid="theme-toggle-mobile"
          >
            {theme === "dark" ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>
          <button
            onClick={() =>
              setOpen(true)
            }
            className="p-2 text-[#078348] dark:text-[#FFC629]"
            aria-label="Menu"
            data-testid="open-mobile-menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>
      {/* ======================================================
          MENU MOBILE
      ====================================================== */}
      {open && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          data-testid="mobile-drawer"
        >
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() =>
              setOpen(false)
            }
          />
          <div className="relative w-72 bg-[#F8F9FA] text-[#292929] dark:bg-[#171717] dark:text-[#E6E6E6] border-l border-[#E2E5E9] dark:border-[#333333] flex flex-col ml-auto">
            <div className="p-4 flex items-center justify-between border-b border-[#E2E5E9] dark:border-[#333333]">
              <BrandLogo className="w-[175px] h-auto" />

              <button
                onClick={() =>
                  setOpen(false)
                }
                data-testid="close-mobile-menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
              {items.map((item) => {
                const Icon =
                  item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={
                      item.to ===
                      "/"
                    }
                    onClick={() =>
                      setOpen(
                        false
                      )
                    }
                    data-testid={`mobile-nav-${
                      item.to.replace(
                        "/",
                        ""
                      ) ||
                      "home"
                    }`}
                    className={({
                      isActive,
                    }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium ${
                        isActive
                          ? "bg-[#FFC629] text-[#171717] shadow-sm"
                          : "text-[#444444] dark:text-[#D4D4D4] hover:bg-[#E9EDF0] dark:hover:bg-[#292929] hover:text-[#078348] dark:hover:text-[#FFC629]"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
            <div className="p-3 border-t border-[#E2E5E9] dark:border-[#333333]">
              <div className="text-sm font-semibold">
                {user.name}
              </div>
              <Badge
                variant="outline"
                className="text-[10px] mt-1"
              >
                {badge}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={
                  handleLogout
                }
                className="w-full mt-3 gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* ======================================================
          CONTEÚDO
      ====================================================== */}
      <main className="flex-1 min-w-0 lg:pt-0 pt-14">
        <Outlet />
      </main>
    </div>
  );
}